/**
 * UpdateModal - UI component for displaying and managing OTA updates
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  VStack,
  HStack,
  Button,
  Text,
  Progress,
  Badge,
  Icon,
  ScrollView,
  Box,
  Divider,
} from 'native-base';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { IStore } from '../../../redux/reducers';
import * as UpdateActions from '../../../redux/actions/updateAction';
import { IUpdateComponent, UpdateType } from '../../../models/Update';
import { getText } from '../../../utils/i18n';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpdateIcon = ({ type }: { type: UpdateType }) => {
  const iconMap = {
    js_bundle: 'code-tags',
    algo_code: 'memory',
    checkpoint: 'content-save',
    ml_runtime_cpu: 'cpu-64-bit',
    ml_runtime_cuda: 'video-3d',
    full: 'cellphone-arrow-down',
  };

  return <Icon as={MaterialCommunityIcons} name={iconMap[type] || 'update'} size="md" />;
};

const UpdateItem: React.FC<{
  update: IUpdateComponent;
  onDownload: () => void;
  onDismiss: () => void;
}> = ({ update, onDownload, onDismiss }) => {
  const downloadProgress = useSelector(
    (store: IStore) => store.update.downloadProgress[update.type]
  );

  const getStatusColor = () => {
    if (!downloadProgress) return 'gray';
    switch (downloadProgress.status) {
      case 'downloading':
        return 'blue';
      case 'ready':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusText = () => {
    if (!downloadProgress) return 'Available';
    switch (downloadProgress.status) {
      case 'downloading':
        return `Downloading ${downloadProgress.progress}%`;
      case 'ready':
        return 'Ready to install';
      case 'failed':
        return 'Failed';
      default:
        return 'Available';
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box p={4} borderWidth={1} borderColor="coolGray.200" borderRadius="md" mb={2}>
      <VStack space={2}>
        <HStack justifyContent="space-between" alignItems="center">
          <HStack space={2} alignItems="center">
            <UpdateIcon type={update.type} />
            <Text fontSize="md" fontWeight="bold">
              {update.type.replace('_', ' ').toUpperCase()}
            </Text>
          </HStack>
          <Badge colorScheme={getStatusColor()}>{getStatusText()}</Badge>
        </HStack>

        <Text fontSize="sm" color="coolGray.600">
          Version: {update.version}
        </Text>

        {update.description && (
          <Text fontSize="sm" color="coolGray.600">
            {update.description}
          </Text>
        )}

        <Text fontSize="xs" color="coolGray.500">
          Size: {formatSize(update.size)}
        </Text>

        {downloadProgress?.status === 'downloading' && (
          <Progress value={downloadProgress.progress} />
        )}

        {downloadProgress?.error && (
          <Text fontSize="xs" color="red.500">
            Error: {downloadProgress.error}
          </Text>
        )}

        <HStack space={2}>
          {!downloadProgress || downloadProgress.status === 'failed' ? (
            <>
              <Button
                flex={1}
                onPress={onDownload}
                colorScheme="blue"
                leftIcon={<Icon as={MaterialCommunityIcons} name="download" />}
              >
                {getText('download')}
              </Button>
              {!update.mandatory && update.type !== 'ml_runtime_cuda' && (
                <Button
                  flex={1}
                  variant="ghost"
                  onPress={onDismiss}
                  colorScheme="gray"
                >
                  {getText('dismiss')}
                </Button>
              )}
            </>
          ) : downloadProgress.status === 'ready' ? (
            <Text fontSize="sm" color="green.600" fontWeight="bold">
              {getText('readyToInstallOnRestart')}
            </Text>
          ) : null}
        </HStack>
      </VStack>
    </Box>
  );
};

export const UpdateModal: React.FC<UpdateModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const availableUpdates = useSelector((store: IStore) => store.update.availableUpdates);
  const downloadProgress = useSelector((store: IStore) => store.update.downloadProgress);
  const updateStatus = useSelector((store: IStore) => store.update.updateStatus);

  const [isApplying, setIsApplying] = useState(false);

  const handleDownload = (update: IUpdateComponent) => {
    dispatch(UpdateActions.downloadUpdate.request(update));
  };

  const handleDismiss = (update: IUpdateComponent) => {
    dispatch(UpdateActions.dismissUpdate(update.type));
  };

  const handleApplyUpdates = () => {
    setIsApplying(true);
    dispatch(UpdateActions.applyUpdates.request());
  };

  const hasReadyUpdates = Object.values(downloadProgress).some(
    (progress) => progress.status === 'ready'
  );

  const allUpdatesReady = availableUpdates.every(
    (update) => downloadProgress[update.type]?.status === 'ready'
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Content maxWidth="500px">
        <Modal.CloseButton />
        <Modal.Header>
          <HStack space={2} alignItems="center">
            <Icon as={MaterialCommunityIcons} name="cellphone-arrow-down" size="md" />
            <Text>{getText('updatesAvailable')}</Text>
          </HStack>
        </Modal.Header>

        <Modal.Body>
          <ScrollView maxH="400px">
            <VStack space={2}>
              {availableUpdates.length === 0 ? (
                <Text textAlign="center" color="coolGray.500">
                  {getText('noUpdatesAvailable')}
                </Text>
              ) : (
                availableUpdates.map((update) => (
                  <UpdateItem
                    key={update.type}
                    update={update}
                    onDownload={() => handleDownload(update)}
                    onDismiss={() => handleDismiss(update)}
                  />
                ))
              )}
            </VStack>
          </ScrollView>
        </Modal.Body>

        <Modal.Footer>
          <Button.Group space={2}>
            <Button variant="ghost" onPress={onClose}>
              {getText('close')}
            </Button>
            {hasReadyUpdates && (
              <Button
                colorScheme="green"
                onPress={handleApplyUpdates}
                isLoading={isApplying}
                isDisabled={!allUpdatesReady}
                leftIcon={<Icon as={MaterialCommunityIcons} name="refresh" />}
              >
                {allUpdatesReady ? getText('installAndRestart') : getText('downloadAllFirst')}
              </Button>
            )}
          </Button.Group>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
